"use client";

import { FormattedMessage } from "react-intl";
import {
  FaLayerGroup,
  FaShieldHalved,
  FaWandMagicSparkles,
} from "react-icons/fa6";

import FlotsIconBlue from "@/public/card/FlotsIconBlue.svg";

import QrCarousel from "@/app/components/card/QrCarousel";

// Titles only, no descriptions: the card has to fit an iPhone 13 screen
// without scrolling, and at a stand nobody reads a paragraph anyway.
const HIGHLIGHTS = [
  { id: "centralize", Icon: FaLayerGroup, titleKey: "card.point1.title" },
  { id: "assistant", Icon: FaWandMagicSparkles, titleKey: "card.point2.title" },
  { id: "trust", Icon: FaShieldHalved, titleKey: "card.point3.title" },
];

/**
 * The Flots business card (/card).
 *
 * Held out on a phone at a stand: full-bleed, no site chrome, no header or
 * footer band — the logo, the three things Flots does, the QR codes and the
 * catchphrase, sized to land in one 390x844 screenful with nothing to scroll
 * to. Language follows the URL (/card vs /card/en), there is no switcher.
 */
export default function BusinessCard() {
  return (
    // Same gradient as the hero section (HeroSection.tsx), so the card reads as
    // the same product when someone opens flots.app right after scanning.
    <main
      className="flex min-h-dvh flex-col"
      style={{ background: "var(--grad-card)" }}
    >
      <article className="mx-auto flex w-full max-w-md flex-1 flex-col px-5 py-6 lg:max-w-5xl lg:px-10 lg:py-8">

        <h1 className="flex items-center justify-center gap-2 text-brand">
          <FlotsIconBlue className="h-9 w-9" aria-hidden="true" />
          <span className="text-2xl font-bold italic leading-none">flots</span>
        </h1>

        <div className="flex flex-1 flex-col justify-center gap-6 lg:grid lg:grid-cols-[1fr_1px_20rem] lg:items-center lg:gap-10">

          {/* What Flots does — headlines only */}
          <ul className="flex min-w-0 flex-col gap-3">
            {HIGHLIGHTS.map(({ id, Icon, titleKey }) => (
              <li key={id} className="flex items-center gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-blue-ulight text-brand">
                  <Icon className="text-base" aria-hidden="true" />
                </span>
                <p className="text-base font-bold leading-tight text-title-blue">
                  <FormattedMessage id={titleKey} />
                </p>
              </li>
            ))}
          </ul>

          {/* Separator, only where the two columns actually sit side by side */}
          <div className="hidden self-stretch bg-gray-light lg:block" aria-hidden="true" />

          <QrCarousel />

          {/* Catchphrase */}
          <div className="text-center lg:col-span-3">
            <p className="text-base font-bold leading-snug text-title-blue lg:text-xl">
              <FormattedMessage id="card.catchphrase" />
            </p>
            <p className="mt-1 text-xs text-gray-main lg:text-sm">
              <FormattedMessage id="card.catchphraseSub" />
            </p>
          </div>
        </div>
      </article>
    </main>
  );
}
